import { Fragment, useMemo } from 'react';
// MUI
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
// CUSTOM COMPONENTS
import { Scrollbar } from '@/components/scrollbar';
// CUSTOM DEFINED HOOK
import useLayout from '@/layouts/layout-2/context/useLayout';
import { useAuth } from '@/hooks/useAuth';
// CUSTOM NAVIGATION DATA
import { navigation } from '@/data/navigation-2';
// STYLED COMPONENTS
import { Dot, LogoBox, MainMenu, SubMenuItem, NavItemButton, SecondarySideBar, MobileSidebarWrapper } from '@/layouts/layout-2/styles';
export default function LayoutSideBar() {
  const {
    active,
    downMd,
    categoryMenus,
    activeSubMenuItem,
    showMobileSideBar,
    openSecondarySideBar,
    handleSubMenuItem,
    handleActiveMainMenu,
    handleCloseMobileSidebar
  } = useLayout();
  const { user } = useAuth();

  // Check if user has club_admin role
  const hasClubAdminRole = useMemo(() => {
    const roles = user?.roles || [];
    return roles.some(role => role.roleType === 'club_admin');
  }, [user?.roles]);

  // Filter and split navigation by position
  const { topNavigation, bottomNavigation } = useMemo(() => {
    const filtered = navigation.filter(nav => {
      if (nav.requiresClubAdmin && !hasClubAdminRole) return false;
      return true;
    });
    return {
      topNavigation: filtered.filter(nav => nav.position !== 'bottom'),
      bottomNavigation: filtered.filter(nav => nav.position === 'bottom')
    };
  }, [hasClubAdminRole]);

  const MAIN_SIDEBAR_CONTENT = <Fragment>
      {/* SIDEBAR LOGO */}
      <LogoBox>
        <Box component="img" src="/static/logo/logo-svg.svg" alt="logo" width={30} />
      </LogoBox>

      {/* NAVIGATION LIST WITH ICON */}
      <Box sx={{
        height: 'calc(100% - 50px)',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Scrollbar sx={{ flexGrow: 1 }}>
          <div className="navigation-list">
            {topNavigation.map((nav, index) => <Tooltip title={nav.name} placement="right" key={index}>
                <NavItemButton disableRipple active={active === nav.name} onClick={handleActiveMainMenu(nav)}>
                  <nav.Icon />
                </NavItemButton>
              </Tooltip>)}
          </div>
        </Scrollbar>
        {bottomNavigation.length > 0 && (
          <div className="navigation-list" style={{ paddingBottom: 16 }}>
            {bottomNavigation.map((nav, index) => <Tooltip title={nav.name} placement="right" key={index}>
                <NavItemButton disableRipple active={active === nav.name} onClick={handleActiveMainMenu(nav)}>
                  <nav.Icon />
                </NavItemButton>
              </Tooltip>)}
          </div>
        )}
      </Box>
    </Fragment>;
  const SECONDARY_SIDEBAR_CONTENT = <Fragment>
      <Typography variant="body1" sx={{
      fontWeight: 500,
      color: 'primary.main',
      padding: '1rem 1.2rem'
    }}>
        {active}
      </Typography>

      <Scrollbar sx={{
      pb: 3
    }}>
        {categoryMenus.map(({
        name,
        path
      }) => <SubMenuItem key={name} active={path === activeSubMenuItem} onClick={() => handleSubMenuItem(path)}>
            <Dot className="dot" />
            <Typography variant="body2" fontSize={13} className="title" fontWeight={500}>
              {name}
            </Typography>
          </SubMenuItem>)}
      </Scrollbar>
    </Fragment>;

  /* SHOW ONLY SMALLER DEVICE - BY DEFAULT IS HIDDEN */
  if (downMd) {
    return <MobileSidebarWrapper show={showMobileSideBar}>
        <div className="main-list">{MAIN_SIDEBAR_CONTENT}</div>

        {showMobileSideBar && <div onClick={handleCloseMobileSidebar} className="backdrop" />}

        {categoryMenus.length > 0 ? <div className="secondary-list-wrapper">
            <div className="list-inner">{SECONDARY_SIDEBAR_CONTENT}</div>
          </div> : null}
      </MobileSidebarWrapper>;
  }
  return <Fragment>
      <MainMenu>{MAIN_SIDEBAR_CONTENT}</MainMenu>
      <SecondarySideBar show={openSecondarySideBar}>{SECONDARY_SIDEBAR_CONTENT}</SecondarySideBar>
    </Fragment>;
}